import { useEffect, useRef, useState } from "react";
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import leadService from '../../services/LeadService';
import adminService from '../../services/AdminService';
import notificationService from '../../services/NotificationService';
import WorkTrackingAnalyticsService from '../../services/WorkTrackingAnalyticsService';
import WorkTrackingUserActivityTab from './WorkTrackingUserActivityTab';
import { Editor } from 'primereact/editor';

const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
};

const feedbackRatingMeta = {
    YES: { label: 'Evet', severity: 'success' },
    PARTLY: { label: 'Kısmen', severity: 'warning' },
    NO: { label: 'Hayır', severity: 'danger' },
};

const workTrackingPeriodLabels = {
    priority: 'Öncelikli işler',
    overdue: 'Gecikmiş',
    today: 'Bugün',
    seven_days: '7 gün',
    thirty_days: '30 gün',
    problem: 'Sorunlu evrak',
    all: 'Tümü',
};

const workTrackingCategoryLabels = {
    all: 'Tümü',
    collection: 'Tahsilatlar',
    payment: 'Ödemeler',
    document: 'Çek ve senetler',
    installment: 'Kart taksitleri',
    stock: 'Stok',
    dashboard: 'Ana sayfa',
};

export default function SystemAdminPage() {
    const [mounted, setMounted] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [userAnalyticsOpened, setUserAnalyticsOpened] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [leads, setLeads] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [leadDialog, setLeadDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [lead, setLead] = useState({ fullName: '', companyName: '', phone: '', email: '', status: 1, note: '' });

    const [passwordDialog, setPasswordDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');

    const [notifications, setNotifications] = useState([]);
    const [notificationDialog, setNotificationDialog] = useState(false);
    const [newNotification, setNewNotification] = useState({ title: '', content: '' });
    const [readUsersDialog, setReadUsersDialog] = useState(false);
    const [readUsers, setReadUsers] = useState([]);
    const [selectedNotif, setSelectedNotif] = useState(null);
    const [loadingNotifications, setLoadingNotifications] = useState(false);
    const [loadingReadUsers, setLoadingReadUsers] = useState(false);
    const [workTrackingAnalytics, setWorkTrackingAnalytics] = useState(null);
    const [loadingWorkTrackingAnalytics, setLoadingWorkTrackingAnalytics] = useState(false);

    const toast = useRef(null);

    const statuses = [
        { label: 'Yeni Kayıt', value: 1 },
        { label: 'Arandı', value: 2 },
        { label: 'Demo Yapıldı', value: 3 },
        { label: 'Aktif Kullanıyor', value: 4 },
        { label: 'Vazgeçti', value: 5 }
    ];

    useEffect(() => {
        loadLeads();
        loadUsers();
        loadNotifications();
        loadWorkTrackingAnalytics();
    }, []);

    useEffect(() => {
        loadLeads();
    }, [searchTerm, selectedStatus]);

    const loadLeads = async () => {
        setLoading(true);
        try {
            const res = await leadService.getAll(searchTerm, selectedStatus);
            if (res.data.success) setLeads(res.data.data);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Leads yüklenemedi' });
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async (search = '') => {
        setLoading(true);
        try {
            const res = await adminService.getAllUsers(search);
            if (res.data.success) setUsers(res.data.data);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Kullanıcılar yüklenemedi' });
        } finally {
            setLoading(false);
        }
    };

    const loadNotifications = async () => {
        setLoadingNotifications(true);
        try {
            const res = await notificationService.getAdminNotifications();
            if (res?.data?.success) {
                setNotifications(res.data.data || []);
            } else {
                setNotifications([]);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Bildirimler yüklenemedi' });
            setNotifications([]);
        } finally {
            setLoadingNotifications(false);
        }
    };

    const loadWorkTrackingAnalytics = async () => {
        setLoadingWorkTrackingAnalytics(true);
        try {
            const res = await WorkTrackingAnalyticsService.getSummary();
            if (res?.success) {
                setWorkTrackingAnalytics(res.data || null);
            } else {
                setWorkTrackingAnalytics(null);
            }
        } catch (error) {
            console.error("İş Takibi kullanım özeti yüklenemedi", error);
            setWorkTrackingAnalytics(null);
        } finally {
            setLoadingWorkTrackingAnalytics(false);
        }
    };

    const saveNotification = async () => {
        if (!newNotification.title || !newNotification.content) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Başlık ve içerik alanları zorunludur' });
            return;
        }
        try {
            const res = await notificationService.createNotification(newNotification);
            if (res?.data?.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Bildirim gönderildi' });
                window.dispatchEvent(new Event("notification-sent"));
                loadNotifications();
                setNotificationDialog(false);
                setNewNotification({ title: '', content: '' });
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: res?.data?.message || 'Bildirim gönderilemedi' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Bildirim gönderilemedi' });
        }
    };

    const openReadUsers = async (notif) => {
        setSelectedNotif(notif);
        setReadUsers([]);
        setReadUsersDialog(true);
        setLoadingReadUsers(true);
        try {
            const res = await notificationService.getReadDetails(notif.id);
            if (res?.data?.success) {
                setReadUsers(res.data.data || []);
            } else {
                setReadUsers([]);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Okuma detayları yüklenemedi' });
            setReadUsers([]);
        } finally {
            setLoadingReadUsers(false);
        }
    };

    const toggleUserStatus = async (user) => {
        try {
            const res = await adminService.toggleUserActive(user.id);
            if (res.data.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: res.data.message });
                loadUsers();
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Kullanıcı durumu güncellenemedi' });
        }
    };

    const openPasswordDialog = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setPasswordDialog(true);
    };

    const savePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Şifre en az 6 karakter olmalıdır' });
            return;
        }
        try {
            const res = await adminService.changeUserPassword(selectedUser.id, newPassword);
            if (res.data.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Şifre değiştirildi' });
                setPasswordDialog(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Şifre değiştirilemedi' });
        }
    };

    const saveLead = async () => {
        try {
            const res = editMode ? await leadService.update(lead) : await leadService.create(lead);
            if (res.data.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Kayıt başarılı' });
                loadLeads();
                setLeadDialog(false);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Hata oluştu' });
        }
    };

    const getStatusSeverity = (s) => {
        const map = { 1: 'info', 2: 'warning', 3: 'help', 4: 'success', 5: 'danger' };
        return map[s] || null;
    };

    const feedbackSummary = workTrackingAnalytics?.feedbackSummary || {};
    const recentFeedbackComments = feedbackSummary.recentComments || [];

    return (
        <div className="card border-none shadow-sm rounded-2xl p-4 bg-white">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <h2 className="mb-4 text-slate-800 font-bold flex items-center gap-2">
                <i className="pi pi-shield text-blue-600"></i> Sistem Yönetimi
            </h2>

            <TabView
                activeIndex={activeTabIndex}
                onTabChange={(event) => {
                    setActiveTabIndex(event.index);
                    if (event.index === 4) setUserAnalyticsOpened(true);
                }}
            >
                <TabPanel header="Lead / Başvurular" leftIcon="pi pi-users mr-2">
                    <div className="flex flex-wrap gap-2 align-items-center justify-content-between mb-4">
                        <div className="flex flex-wrap gap-2">
                            <span className="p-input-icon-left">
                                <i className="pi pi-search" />
                                <InputText type="search" onInput={(e) => setSearchTerm(e.target.value)} placeholder="Ara..." className="p-inputtext-sm" />
                            </span>
                            <Dropdown value={selectedStatus} options={statuses} onChange={(e) => setSelectedStatus(e.value)}  placeholder="Durum Seç" showClear className="p-inputtext-sm" />
                            <Button label="Yeni Ekle" icon="pi pi-plus" severity="success" onClick={() => { setLead({ fullName: '', companyName: '', phone: '', email: '', status: 1, note: '' }); setEditMode(false); setLeadDialog(true); }} className="p-button-sm" />
                        </div>
                    </div>
                    <DataTable value={leads} loading={loading} rows={10} paginator responsiveLayout="stack" breakpoint="960px" className="p-datatable-sm" sortField="createdDate" sortOrder={-1}>
                        <Column field="fullName" header="Ad Soyad" sortable />
                        <Column field="companyName" header="Firma" sortable />
                        <Column field="phone" header="Telefon" sortable />
                        <Column field="email" header="E-posta" sortable />
                        <Column field="createdDate" header="Kayıt Tarihi" sortable body={(r) => new Date(r.createdDate).toLocaleDateString('tr-TR')} />
                        <Column field="status" header="Durum" body={(r) => <Tag value={r.statusText} severity={getStatusSeverity(r.status)} />} sortable />
                        <Column body={(r) => <Button icon="pi pi-pencil" rounded text severity="info" onClick={() => { setLead({ ...r }); setEditMode(true); setLeadDialog(true); }} />} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="Sistem Kullanıcıları" leftIcon="pi pi-user mr-2">
                    <div className="flex flex-wrap gap-2 align-items-center justify-content-between mb-4">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText type="search" onInput={(e) => loadUsers(e.target.value)} placeholder="Kullanıcı Ara..." className="p-inputtext-sm" />
                        </span>
                    </div>
                    <DataTable value={users} loading={loading} rows={10} paginator responsiveLayout="stack" breakpoint="960px" className="p-datatable-sm">
                        <Column field="fullName" header="Ad Soyad" sortable />
                        <Column field="email" header="E-posta" sortable />
                        <Column field="tenantName" header="Şirket" sortable />
                        <Column field="role" header="Rol" sortable />
                        <Column field="lastLoginDate" header="Son Giriş" body={(r) => r.lastLoginDate ? new Date(r.lastLoginDate).toLocaleString('tr-TR') : '-'} sortable />
                        <Column field="isActive" header="Aktif/Pasif" body={(r) => (
                            <div className="flex items-center gap-2">
                                <InputSwitch checked={r.isActive} onChange={() => toggleUserStatus(r)} />
                                <Tag severity={r.isActive ? 'success' : 'danger'} value={r.isActive ? 'Aktif' : 'Pasif'} />
                            </div>
                        )} />
                        <Column body={(r) => (
                            <Button icon="pi pi-key" label="Şifre Değiştir" rounded text severity="warning" onClick={() => openPasswordDialog(r)} className="p-button-sm" />
                        )} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="Bildirimler & Duyurular" leftIcon="pi pi-bell mr-2">
                    <div className="flex flex-wrap gap-2 align-items-center justify-content-between mb-4">
                        <Button
                            label="Yeni Bildirim Gönder"
                            icon="pi pi-plus"
                            severity="success"
                            onClick={() => {
                                setNewNotification({ title: '', content: '' });
                                setNotificationDialog(true);
                            }}
                            className="p-button-sm"
                        />
                    </div>
                    <DataTable
                        value={notifications}
                        loading={loadingNotifications}
                        rows={10}
                        paginator
                        responsiveLayout="stack"
                        breakpoint="960px"
                        className="p-datatable-sm"
                    >
                        <Column field="createdDate" header="Tarih" body={(r) => new Date(r.createdDate).toLocaleString('tr-TR')} sortable />
                        <Column field="title" header="Başlık" sortable />
                        <Column field="content" header="İçerik" body={(r) => <span className="truncate block max-w-xs md:max-w-md">{stripHtml(r.content)}</span>} />
                        <Column field="readCount" header="Okunma Sayısı" body={(r) => (
                            <Tag value={`${r.readCount} kullanıcı`} severity={r.readCount > 0 ? "info" : "warning"} />
                        )} sortable />
                        <Column body={(r) => (
                            <Button
                                icon="pi pi-eye"
                                label="Görenler"
                                rounded
                                text
                                severity="help"
                                onClick={() => openReadUsers(r)}
                                className="p-button-sm"
                            />
                        )} />
                    </DataTable>
                </TabPanel>

                <TabPanel header="İş Takibi Kullanımı" leftIcon="pi pi-chart-line mr-2">
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <h3 className="m-0 text-base font-black text-slate-800">Son 30 Gün</h3>
                            <p className="mb-0 mt-1 text-xs font-medium text-slate-500">Müşteri adı, açıklama ve finansal tutar içermeyen teknik kullanım özeti.</p>
                        </div>
                        <Button
                            label="Yenile"
                            icon="pi pi-refresh"
                            outlined
                            loading={loadingWorkTrackingAnalytics}
                            onClick={loadWorkTrackingAnalytics}
                            className="p-button-sm !rounded-xl"
                        />
                    </div>

                    {loadingWorkTrackingAnalytics ? (
                        <div className="flex min-h-52 items-center justify-center text-slate-400">
                            <i className="pi pi-spin pi-spinner mr-2 text-xl" /> Kullanım özeti yükleniyor...
                        </div>
                    ) : !workTrackingAnalytics ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center">
                            <i className="pi pi-chart-line text-3xl text-slate-300" />
                            <p className="mb-0 mt-3 text-sm font-bold text-slate-500">Henüz kullanım verisi bulunmuyor veya özet alınamadı.</p>
                        </div>
                    ) : (
                        <div className="space-y-5">
                            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                                {[
                                    { label: "İşletme", value: workTrackingAnalytics.uniqueTenantCount, icon: "pi pi-building", tone: "text-indigo-600 bg-indigo-50" },
                                    { label: "Kullanıcı", value: workTrackingAnalytics.uniqueUserCount, icon: "pi pi-users", tone: "text-blue-600 bg-blue-50" },
                                    { label: "Ziyaret", value: workTrackingAnalytics.visitCount, icon: "pi pi-eye", tone: "text-emerald-600 bg-emerald-50" },
                                    { label: "Aksiyon", value: workTrackingAnalytics.actionClickCount, icon: "pi pi-bolt", tone: "text-orange-600 bg-orange-50" },
                                    { label: "Toplam Olay", value: workTrackingAnalytics.totalEvents, icon: "pi pi-chart-bar", tone: "text-violet-600 bg-violet-50" },
                                ].map((metric) => (
                                    <div key={metric.label} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                                        <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${metric.tone}`}>
                                            <i className={metric.icon} />
                                        </div>
                                        <strong className="block text-2xl font-black text-slate-900">{(metric.value || 0).toLocaleString('tr-TR')}</strong>
                                        <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{metric.label}</span>
                                    </div>
                                ))}
                            </div>

                            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                                <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                                    <h4 className="m-0 text-xs font-black uppercase tracking-wider text-slate-700">İş Takibi Geri Bildirimleri</h4>
                                    <p className="mb-0 mt-1 text-xs font-medium text-slate-500">Puanlar ve son kullanıcı yorumları kimlik bilgisi gösterilmeden sunulur.</p>
                                </div>

                                <div className="grid gap-3 p-4 sm:grid-cols-3">
                                    {[
                                        { key: 'YES', value: feedbackSummary.yesCount, tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
                                        { key: 'PARTLY', value: feedbackSummary.partlyCount, tone: 'border-amber-200 bg-amber-50 text-amber-700' },
                                        { key: 'NO', value: feedbackSummary.noCount, tone: 'border-rose-200 bg-rose-50 text-rose-700' },
                                    ].map((rating) => (
                                        <div key={rating.key} className={`rounded-2xl border p-4 ${rating.tone}`}>
                                            <strong className="block text-2xl font-black">{(rating.value || 0).toLocaleString('tr-TR')}</strong>
                                            <span className="text-[10px] font-black uppercase tracking-wider">{feedbackRatingMeta[rating.key].label}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t border-slate-200 px-4 py-3">
                                    <h5 className="m-0 text-xs font-black uppercase tracking-wider text-slate-600">Son Yorumlar</h5>
                                </div>
                                {recentFeedbackComments.length === 0 ? (
                                    <p className="m-0 border-t border-slate-100 px-4 py-6 text-center text-xs font-semibold text-slate-400">Seçilen dönemde yazılı yorum bulunmuyor.</p>
                                ) : (
                                    <div className="divide-y divide-slate-100">
                                        {recentFeedbackComments.map((feedback, index) => {
                                            const rating = feedbackRatingMeta[feedback.rating] || { label: feedback.rating || 'Bilinmiyor', severity: 'secondary' };
                                            const period = workTrackingPeriodLabels[feedback.selectedPeriod] || feedback.selectedPeriod;
                                            const category = workTrackingCategoryLabels[feedback.selectedCategory] || feedback.selectedCategory;

                                            return (
                                                <article key={`${feedback.createdDate}-${index}`} className="p-4">
                                                    <div className="mb-2 flex flex-wrap items-center gap-2">
                                                        <Tag value={rating.label} severity={rating.severity} />
                                                        <time className="text-[10px] font-bold text-slate-400">
                                                            {new Date(feedback.createdDate).toLocaleString('tr-TR')}
                                                        </time>
                                                        {(period || category) && (
                                                            <span className="text-[10px] font-bold text-slate-400">
                                                                {[period, category].filter(Boolean).join(' / ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="m-0 whitespace-pre-wrap break-words text-sm font-medium text-slate-700">{feedback.comment}</p>
                                                </article>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>

                            <div className="grid gap-4 lg:grid-cols-2">
                                <div className="overflow-hidden rounded-2xl border border-slate-200">
                                    <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-600">Olay Türleri</div>
                                    <DataTable value={workTrackingAnalytics.eventTypeBreakdown || []} emptyMessage="Olay bulunamadı" className="p-datatable-sm">
                                        <Column field="key" header="Olay" />
                                        <Column field="count" header="Adet" body={(row) => <Tag value={row.count.toLocaleString('tr-TR')} severity="info" />} />
                                    </DataTable>
                                </div>
                                <div className="overflow-hidden rounded-2xl border border-slate-200">
                                    <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black uppercase tracking-wider text-slate-600">İş Kaynakları</div>
                                    <DataTable value={workTrackingAnalytics.sourceTypeBreakdown || []} emptyMessage="Kaynak bulunamadı" className="p-datatable-sm">
                                        <Column field="key" header="Kaynak" />
                                        <Column field="count" header="Adet" body={(row) => <Tag value={row.count.toLocaleString('tr-TR')} severity="success" />} />
                                    </DataTable>
                                </div>
                            </div>
                        </div>
                    )}
                </TabPanel>

                <TabPanel header="Kullanıcı Bazlı İş Takibi" leftIcon="pi pi-user-edit mr-2">
                    {userAnalyticsOpened ? <WorkTrackingUserActivityTab /> : null}
                </TabPanel>
            </TabView>

            {/* Lead Dialog */}
            <Dialog visible={leadDialog} style={{ width: '450px', height: '90vh' }} header={editMode ? "Kayıt Düzenle" : "Yeni Kayıt"} modal className="p-fluid" footer={
                <div className="flex justify-end gap-2"><Button label="İptal" icon="pi pi-times" text onClick={() => setLeadDialog(false)} /><Button label="Kaydet" icon="pi pi-check" onClick={saveLead} /></div>
            } onHide={() => setLeadDialog(false)}>
                {!editMode && (
                    <>
                        <div className="field mb-3"><label className="font-bold block mb-2 text-sm">Ad Soyad</label><InputText value={lead.fullName} onChange={(e) => setLead({ ...lead, fullName: e.target.value })} required autoFocus className="!py-2.5 !rounded-xl" /></div>
                        <div className="field mb-3"><label className="font-bold block mb-2 text-sm">Firma Adı</label><InputText value={lead.companyName} onChange={(e) => setLead({ ...lead, companyName: e.target.value })} required className="!py-2.5 !rounded-xl" /></div>
                        <div className="field mb-3"><label className="font-bold block mb-2 text-sm">Telefon</label><InputText value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} required className="!py-2.5 !rounded-xl" /></div>
                        <div className="field mb-3"><label className="font-bold block mb-2 text-sm">E-posta</label><InputText value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required className="!py-2.5 !rounded-xl" /></div>
                    </>
                )}
                <div className="field mb-3"><label className="font-bold block mb-2 text-sm">Durum</label><Dropdown value={lead.status} options={statuses} onChange={(e) => setLead({ ...lead, status: e.value })} placeholder="Durum Seç" className="!rounded-xl" appendTo="self" /></div>
                <div className="field mb-3"><label className="font-bold block mb-2 text-sm">Not</label><InputTextarea value={lead.note} onChange={(e) => setLead({ ...lead, note: e.target.value })} rows={3} className="!rounded-xl" /></div>
            </Dialog>

            {/* Password Dialog */}
            <Dialog visible={passwordDialog} style={{ width: '400px' }} header="Şifre Değiştir" modal className="p-fluid" footer={
                <div className="flex justify-end gap-2"><Button label="İptal" icon="pi pi-times" text onClick={() => setPasswordDialog(false)} /><Button label="Kaydet" icon="pi pi-check" onClick={savePassword} /></div>
            } onHide={() => setPasswordDialog(false)}>
                <div className="field">
                    <label className="font-bold block mb-2">Kullanıcı: {selectedUser?.fullName}</label>
                    <InputText value={newPassword} onChange={(e) => setNewPassword(e.target.value)} type="password" placeholder="Yeni şifre girin..." className="!py-2.5 !rounded-xl" />
                </div>
            </Dialog>

            {/* Yeni Bildirim Dialog */}
            <Dialog
                visible={notificationDialog}
                style={{ width: '900px' }}
                header="Yeni Bildirim Gönder"
                modal
                className="p-fluid !rounded-2xl"
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="İptal" icon="pi pi-times" text onClick={() => setNotificationDialog(false)} />
                        <Button label="Gönder" icon="pi pi-check" onClick={saveNotification} />
                    </div>
                }
                onHide={() => setNotificationDialog(false)}
            >
                <div className="field mb-3">
                    <label className="font-bold block mb-2 text-sm">Bildirim Başlığı</label>
                    <InputText
                        value={newNotification.title}
                        onChange={(e) => setNewNotification({ ...newNotification, title: e.target.value })}
                        required
                        autoFocus
                        className="!py-2.5 !rounded-xl"
                        placeholder="Örn: Yeni Özellik Eklendi!"
                    />
                </div>
                <div className="field mb-3">
                    <label className="font-bold block mb-2 text-sm">Bildirim İçeriği / Detayı (Rich Text)</label>
                    <Editor
                        value={newNotification.content}
                        onTextChange={(e) => setNewNotification({ ...newNotification, content: e.htmlValue })}
                        style={{ height: '320px' }}
                    />
                </div>
            </Dialog>

            {/* Bildirimi Görenler Dialog */}
            <Dialog
                visible={readUsersDialog}
                style={{ width: '600px' }}
                header={`Bildirimi Okuyanlar: ${selectedNotif?.title || ''}`}
                modal
                className="p-fluid !rounded-2xl"
                onHide={() => setReadUsersDialog(false)}
                footer={
                    <div className="flex justify-end">
                        <Button label="Kapat" icon="pi pi-times" text onClick={() => setReadUsersDialog(false)} />
                    </div>
                }
            >
                <DataTable
                    value={readUsers}
                    loading={loadingReadUsers}
                    rows={10}
                    paginator
                    emptyMessage="Bu bildirimi henüz gören olmamış."
                    className="p-datatable-sm"
                >
                    <Column field="fullName" header="Kullanıcı Adı" sortable />
                    <Column field="email" header="E-posta" sortable />
                    <Column field="tenantName" header="Şirket / Firma" sortable />
                    <Column field="readDate" header="Okuma Tarihi" body={(r) => new Date(r.readDate).toLocaleString('tr-TR')} sortable />
                </DataTable>
            </Dialog>
        </div>
    );
}
